import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Plus, RefreshCw, ShoppingBag, Unplug, X, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import PhoneSelector from "../components/PhoneSelector";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import {
  connectCatalog,
  createCatalog,
  createProduct,
  disconnectCatalog,
  getCommerceSettings,
  listCatalogs,
  listOrders,
  listProducts,
  updateCommerceSettings,
  generateFbCatalogLink,
} from "../apis/catalog";

function apiError(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
}

function isManageCatalogError(err) {
  const data = err?.response?.data || {};
  if (data.code === "CATALOG_MANAGE_PERMISSION") return true;
  const blob = `${data.error || ""} ${data.aisensy?.error_user_msg || ""} ${data.aisensy?.error_subcode || ""}`;
  return /2388100|manage catalogue permission/i.test(blob);
}

function slugify(name) {
  return String(name || "item")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "item";
}

function rupeesToPaise(rupees) {
  const n = Number(rupees);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

function formatPrice(value) {
  if (value == null || value === "") return "—";
  const raw = String(value).replace(/[^\d.]/g, "");
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(value);
  if (n >= 100 && Number.isInteger(n)) return `₹${(n / 100).toFixed(2)}`;
  return `₹${n.toFixed(2)}`;
}

const Commerce = () => {
  const { selectedPhoneId } = usePhoneAccount();
  const location = useLocation();
  const navigate = useNavigate();
  // Catalog tab hidden until Meta Manage-catalogue attach works.
  const commerceTab = "orders";
  const [catalogs, setCatalogs] = useState([]);
  const [connectedId, setConnectedId] = useState(null);
  const [selectedCatalogId, setSelectedCatalogId] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ enableCatalog: false, enableCart: true });
  const [loading, setLoading] = useState(false);
  const [gateError, setGateError] = useState(null);
  const [showCreateCatalog, setShowCreateCatalog] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [catalogName, setCatalogName] = useState("");
  const [creating, setCreating] = useState(false);
  const [connectingFb, setConnectingFb] = useState(false);
  const [needFbGrant, setNeedFbGrant] = useState(false);
  const [productsBlocked, setProductsBlocked] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    retailer_id: "",
    price: "150",
    image_url: "",
    description: "",
    brand: "",
    sale_price: "",
    sale_price_start_date: "",
    sale_price_end_date: "",
  });

  const loadAll = useCallback(async () => {
    if (!selectedPhoneId) {
      setCatalogs([]);
      setProducts([]);
      setOrders([]);
      setGateError(null);
      setSelectedCatalogId(null);
      return;
    }
    setLoading(true);
    setGateError(null);
    try {
      const [catRes, setRes, orderRes] = await Promise.all([
        listCatalogs(selectedPhoneId).catch(() => ({ data: { catalogs: [] } })),
        getCommerceSettings(selectedPhoneId).catch(() => ({ data: {} })),
        listOrders(selectedPhoneId).catch(() => ({ data: { orders: [] } })),
      ]);
      const list = catRes.data?.catalogs || [];
      const connected = catRes.data?.connected_catalog_id || setRes.data?.connected_catalog_id || null;
      const ids = list.map((c) => String(c.id || c.catalog_id || "")).filter(Boolean);
      setCatalogs(list);
      setConnectedId(connected);
      setSelectedCatalogId((prev) => {
        if (prev && ids.includes(String(prev))) return String(prev);
        if (connected && ids.includes(String(connected))) return String(connected);
        return ids[0] || null;
      });
      setOrders(orderRes.data?.orders || []);
      const s = setRes.data?.settings || {};
      setSettings({
        enableCatalog: Boolean(s.is_catalog_visible ?? s.enableCatalog ?? setRes.data?.commerce_catalog_enabled),
        enableCart: s.cart_enabled ?? s.enableCart ?? setRes.data?.commerce_cart_enabled !== false,
      });
    } catch (err) {
      const status = err?.response?.status;
      const message = apiError(err, "Failed to load catalogs");
      if (status === 409) setGateError(message);
      else toast.error(message);
      setCatalogs([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("tab") === "catalog") {
      navigate("/commerce?tab=orders", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!selectedPhoneId || !selectedCatalogId) {
      setProducts([]);
      setProductsBlocked(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const prodRes = await listProducts(selectedPhoneId, { catalogId: selectedCatalogId });
        if (!cancelled) {
          setProductsBlocked(false);
          setProducts(prodRes.data?.products || []);
        }
      } catch (err) {
        if (!cancelled) {
          setProducts([]);
          if (isManageCatalogError(err)) setProductsBlocked(true);
          else toast.error(apiError(err, "Failed to load products"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPhoneId, selectedCatalogId]);

  const paisePreview = useMemo(() => {
    const p = rupeesToPaise(productForm.price);
    return p == null ? null : `API will send ${p} paise → WhatsApp shows ₹${(p / 100).toFixed(2)}`;
  }, [productForm.price]);

  const handleCreateCatalog = async (e) => {
    e.preventDefault();
    if (!catalogName.trim()) return;
    setCreating(true);
    try {
      const created = await createCatalog(selectedPhoneId, { name: catalogName.trim() });
      const newId =
        created.data?.catalog?.id ||
        created.data?.catalog?.catalog_id ||
        created.data?.raw?.id;
      if (newId) setSelectedCatalogId(String(newId));
      toast.success("Catalog created. Add products below. Show on WhatsApp is a separate step.");
      setShowCreateCatalog(false);
      setCatalogName("");
      await loadAll();
    } catch (err) {
      toast.error(apiError(err, "Failed to create catalog"));
    } finally {
      setCreating(false);
    }
  };

  const handleConnect = async (catalogId) => {
    if (connectedId && connectedId !== catalogId) {
      if (!window.confirm("WhatsApp allows one catalog per number. Disconnect the current catalog first?")) return;
      try {
        await disconnectCatalog(selectedPhoneId, connectedId);
      } catch (err) {
        toast.error(apiError(err, "Failed to disconnect current catalog"));
        return;
      }
    }
    try {
      await connectCatalog(selectedPhoneId, catalogId);
      setNeedFbGrant(false);
      toast.success("Catalog connected. Enable storefront visibility below so customers can see it.");
      await loadAll();
    } catch (err) {
      if (isManageCatalogError(err)) setNeedFbGrant(true);
      toast.error(apiError(err, "Failed to attach catalog to WhatsApp"));
    }
  };

  const handleDisconnect = async (catalogId) => {
    if (!window.confirm("Disconnect this catalog from the WhatsApp number?")) return;
    try {
      await disconnectCatalog(selectedPhoneId, catalogId);
      toast.success("Catalog disconnected");
      await loadAll();
    } catch (err) {
      toast.error(apiError(err, "Failed to disconnect catalog"));
    }
  };

  const handleSettings = async (next) => {
    setSettings(next);
    try {
      await updateCommerceSettings(selectedPhoneId, next);
      toast.success("Commerce settings updated");
    } catch (err) {
      toast.error(apiError(err, "Failed to update settings"));
      loadAll();
    }
  };

  const handleFbCatalogConnect = async () => {
    if (!selectedPhoneId) return;
    setConnectingFb(true);
    const popup = window.open("", "_blank");
    try {
      const res = await generateFbCatalogLink(selectedPhoneId);
      const url = res.data?.url;
      if (!url) throw new Error("No catalog connect URL returned");
      if (popup) {
        popup.opener = null;
        popup.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      toast.success("In Facebook, select this catalog (Manage catalogue), then come back and tap Connect.");
    } catch (err) {
      if (popup) popup.close();
      toast.error(apiError(err, "Failed to open Facebook catalog connect"));
    } finally {
      setConnectingFb(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!selectedCatalogId) {
      toast.error("Select a catalog first");
      return;
    }
    const price = rupeesToPaise(productForm.price);
    if (!productForm.name.trim() || !price) {
      toast.error("Name and a positive price are required");
      return;
    }
    const toDdMmYyyy = (iso) => {
      if (!iso) return undefined;
      const [y, m, d] = iso.split("-");
      return `${d}-${m}-${y}`;
    };
    setCreating(true);
    try {
      await createProduct(selectedPhoneId, {
        catalogId: selectedCatalogId,
        name: productForm.name.trim(),
        retailer_id: productForm.retailer_id.trim() || slugify(productForm.name),
        price,
        currency: "INR",
        image_url: productForm.image_url.trim() || undefined,
        description: productForm.description.trim() || undefined,
        brand: productForm.brand.trim() || undefined,
        sale_price: productForm.sale_price ? rupeesToPaise(productForm.sale_price) : undefined,
        sale_price_start_date: toDdMmYyyy(productForm.sale_price_start_date),
        sale_price_end_date: toDdMmYyyy(productForm.sale_price_end_date),
      });
      toast.success("Product created");
      setShowCreateProduct(false);
      setProductForm((p) => ({ ...p, name: "", retailer_id: "", description: "" }));
      const prodRes = await listProducts(selectedPhoneId, { catalogId: selectedCatalogId });
      setProducts(prodRes.data?.products || []);
      loadAll();
    } catch (err) {
      toast.error(apiError(err, "Failed to create product"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <PhoneSelector section="phone_numbers" />
      </div>
      <div className="page-head" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{commerceTab === "orders" ? "Orders" : "Catalog"}</h2>
          <p style={{ color: "var(--text-2)", fontSize: 14, marginTop: 2 }}>
            {commerceTab === "orders"
              ? "Carts received on WhatsApp — also visible as bubbles in live chat"
              : "Add products in a Facebook catalog. Connect is only to show the shop on this WhatsApp number"}
          </p>
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={loadAll} disabled={!selectedPhoneId || loading} className="ping-btn ping-btn-ghost ping-btn-sm">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          Refresh
        </button>
        {commerceTab === "catalog" && (
          <button
            type="button"
            onClick={handleFbCatalogConnect}
            disabled={!selectedPhoneId || connectingFb}
            className="ping-btn ping-btn-ghost ping-btn-sm"
          >
            {connectingFb ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
            Connect Facebook Catalog
          </button>
        )}
        {commerceTab === "catalog" && (
          <button
            type="button"
            onClick={() => setShowCreateCatalog(true)}
            disabled={!selectedPhoneId || !!gateError}
            className="ping-btn ping-btn-primary ping-btn-sm"
          >
            <Plus size={16} /> New catalog
          </button>
        )}
      </div>
      <div className="ping-pill-tabs">
        {/* Catalog tab hidden until Meta Manage-catalogue attach works.
        <button type="button" className={`ping-ptab ${commerceTab === "catalog" ? "on" : ""}`} onClick={() => navigate("/commerce?tab=catalog")}>Catalog</button>
        */}
        <button type="button" className={`ping-ptab ${commerceTab === "orders" ? "on" : ""}`} onClick={() => navigate("/commerce?tab=orders")}>Orders</button>
      </div>

        {gateError && commerceTab === "catalog" && (
          <div className="ping-banner amber" style={{ marginBottom: 14 }}>
            <div>
              <p style={{ margin: 0 }}>{gateError}</p>
              <p style={{ margin: "8px 0 0", fontSize: 13 }}>
                Facebook catalog permission is missing on this number. Connect Facebook Catalog, finish the Facebook
                popup, then refresh.
              </p>
            </div>
            <button
              type="button"
              onClick={handleFbCatalogConnect}
              disabled={!selectedPhoneId || connectingFb}
              className="ping-btn ping-btn-primary ping-btn-sm"
              style={{ marginTop: 10 }}
            >
              {connectingFb ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
              Connect Facebook Catalog
            </button>
          </div>
        )}

        {needFbGrant && !gateError && commerceTab === "catalog" && (
          <div className="ping-banner amber" style={{ marginBottom: 14 }}>
            <div>
              <p style={{ margin: 0 }}>
                Facebook login worked, so catalogs and products load. <b>Connect</b> is a second Meta step: attach this
                catalog to the WhatsApp number for the shop icon.
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 13 }}>
                You can add products on the selected catalog now. To show the shop on WhatsApp, open WhatsApp Manager →
                Catalog → Choose a catalogue, or tap Connect Facebook Catalog again and tick this catalog.
              </p>
            </div>
            <button
              type="button"
              onClick={handleFbCatalogConnect}
              disabled={!selectedPhoneId || connectingFb}
              className="ping-btn ping-btn-primary ping-btn-sm"
              style={{ marginTop: 10 }}
            >
              {connectingFb ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
              Connect Facebook Catalog
            </button>
          </div>
        )}

        {!selectedPhoneId ? (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-16 text-center text-sm text-gray-600">
            Select a WhatsApp number to manage commerce.
          </div>
        ) : loading && catalogs.length === 0 && !gateError ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading…
          </div>
        ) : (
          <>
            {commerceTab === "catalog" && connectedId && !settings.enableCatalog && !gateError && (
              <div className="mb-4 rounded-2xl border border-sky-200 bg-sky-50 px-5 py-3 text-sm text-sky-900">
                Catalog is connected but hidden on the WhatsApp profile. Turn on <b>Show catalog on profile</b> below.
              </div>
            )}

            {commerceTab === "catalog" && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
              {catalogs.map((cat) => {
                const id = cat.id || cat.catalog_id;
                const connected = String(id) === String(connectedId);
                const selected = String(id) === String(selectedCatalogId);
                return (
                  <div
                    key={id}
                    className="ping-card"
                    style={selected ? { outline: "2px solid var(--ping-green, #16a34a)", outlineOffset: 0 } : undefined}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedCatalogId(String(id))}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{cat.name || "Untitled"}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {cat.product_count ?? cat.products_count ?? 0} products
                            {selected ? " · editing" : ""}
                          </p>
                        </div>
                        {connected && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                            On WhatsApp
                          </span>
                        )}
                      </div>
                    </button>
                    <div className="mt-3 flex gap-2">
                      {connected ? (
                        <button
                          type="button"
                          onClick={() => handleDisconnect(id)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200"
                        >
                          <Unplug size={13} /> Remove from WhatsApp
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleConnect(id)}
                          className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg ping-btn ping-btn-ghost ping-btn-sm"
                        >
                          Show on WhatsApp
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!gateError && catalogs.length === 0 && (
                <div className="col-span-full rounded-2xl border border-dashed border-gray-300 px-6 py-12 text-center">
                  <ShoppingBag className="mx-auto mb-2 text-gray-400" size={32} />
                  <p className="text-sm text-gray-600">No catalogs yet. Create one to start adding products.</p>
                </div>
              )}
            </div>
            </>
            )}

            {commerceTab === "catalog" && selectedCatalogId && (
              <>
                {connectedId && String(connectedId) === String(selectedCatalogId) && (
                <div className="ping-card mb-6">
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">WhatsApp storefront</h2>
                  <label className="flex items-center justify-between py-2 text-sm">
                    <span>Show catalog on profile</span>
                    <input
                      type="checkbox"
                      checked={!!settings.enableCatalog}
                      onChange={(e) => handleSettings({ ...settings, enableCatalog: e.target.checked })}
                    />
                  </label>
                  <label className="flex items-center justify-between py-2 text-sm">
                    <span>Enable cart</span>
                    <input
                      type="checkbox"
                      checked={!!settings.enableCart}
                      onChange={(e) => handleSettings({ ...settings, enableCart: e.target.checked })}
                    />
                  </label>
                </div>
                )}
                {productsBlocked && (
                  <p className="text-sm text-amber-800 mb-3">
                    This catalog is visible, but Facebook has not granted <b>Manage</b> on it, so products cannot load
                    yet. Open Connect Facebook Catalog and tick this catalog under Manage catalogues, then Refresh.
                  </p>
                )}
                {!productsBlocked && String(connectedId) !== String(selectedCatalogId) && (
                  <p className="text-sm text-gray-600 mb-3">
                    Products save to Facebook on this catalog. Showing the shop on the WhatsApp profile still needs
                    Show on WhatsApp.
                  </p>
                )}

                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">Products</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setProductForm((p) => ({ ...p, retailer_id: slugify(p.name) }));
                      setShowCreateProduct(true);
                    }}
                    className="ping-btn ping-btn-primary ping-btn-sm"
                  >
                    <Plus size={14} /> Add product
                  </button>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-4 py-2">Name</th>
                        <th className="px-4 py-2">SKU</th>
                        <th className="px-4 py-2">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                            No products in this catalog yet.
                          </td>
                        </tr>
                      ) : (
                        products.map((p) => (
                          <tr key={p.id || p.retailer_id} className="border-t border-gray-100">
                            <td className="px-4 py-2">{p.name}</td>
                            <td className="px-4 py-2 font-mono text-xs">{p.retailer_id || p.retailerId}</td>
                            <td className="px-4 py-2">{formatPrice(p.price)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {commerceTab === "orders" && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Catalog orders</h2>
              <p className="text-sm text-gray-600 mb-3">
                Carts customers send from WhatsApp. Each order also appears in that customer’s Live Chat.
              </p>
              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-4 py-2">When</th>
                      <th className="px-4 py-2">Customer</th>
                      <th className="px-4 py-2">Items</th>
                      <th className="px-4 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                          No catalog orders yet. When a customer taps Send cart in WhatsApp, it will show here.
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => {
                        const items = Array.isArray(order.items) ? order.items : [];
                        return (
                          <tr key={order.message_id || order.id} className="border-t border-gray-100 align-top">
                            <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {order.ordered_at || order.created_at
                                ? new Date(order.ordered_at || order.created_at).toLocaleString()
                                : "—"}
                            </td>
                            <td className="px-4 py-2">
                              <p className="font-medium text-gray-900">{order.customer_name || "Customer"}</p>
                              <p className="text-xs font-mono text-gray-500">{order.customer_wa_id}</p>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-700">
                              {items.length === 0
                                ? `${order.item_count || 0} item(s)`
                                : items.map((item, i) => (
                                    <p key={i}>
                                      {item.name || item.product_retailer_id || "Item"} × {item.quantity || 1}
                                    </p>
                                  ))}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {order.total_amount != null
                                ? `${order.currency || "INR"} ${order.total_amount}`
                                : "—"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </>
        )}

      {showCreateCatalog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <form onSubmit={handleCreateCatalog} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">New catalog</h2>
              <button type="button" onClick={() => setShowCreateCatalog(false)}><X size={18} /></button>
            </div>
            <input
              value={catalogName}
              onChange={(e) => setCatalogName(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
              placeholder="Store catalog"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreateCatalog(false)} className="px-4 py-2 text-sm rounded-xl border">Cancel</button>
              <button type="submit" disabled={creating} className="ping-btn ping-btn-primary ping-btn-sm">
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreateProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <form onSubmit={handleCreateProduct} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add product</h2>
              <button type="button" onClick={() => setShowCreateProduct(false)}><X size={18} /></button>
            </div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              value={productForm.name}
              onChange={(e) => setProductForm((p) => ({
                ...p,
                name: e.target.value,
                retailer_id: p.retailer_id || slugify(e.target.value),
              }))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-3"
            />
            <label className="block text-sm font-medium mb-1">SKU (retailer_id)</label>
            <input
              value={productForm.retailer_id}
              onChange={(e) => setProductForm((p) => ({ ...p, retailer_id: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-3 font-mono"
            />
            <label className="block text-sm font-medium mb-1">Price (₹)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={productForm.price}
              onChange={(e) => setProductForm((p) => ({ ...p, price: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            />
            {paisePreview && <p className="text-xs text-gray-500 mt-1 mb-3">{paisePreview}</p>}
            <label className="block text-sm font-medium mb-1">Image URL (https)</label>
            <input
              value={productForm.image_url}
              onChange={(e) => setProductForm((p) => ({ ...p, image_url: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-3"
            />
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={productForm.description}
              onChange={(e) => setProductForm((p) => ({ ...p, description: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-3"
              rows={2}
            />
            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreateProduct(false)} className="px-4 py-2 text-sm rounded-xl border">Cancel</button>
              <button type="submit" disabled={creating} className="ping-btn ping-btn-primary ping-btn-sm">
                {creating ? "Saving…" : "Add"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Commerce;
